import React, { useMemo } from 'react'
import { FormControl, MenuItem, Select } from '@mui/material'
import { LuChevronDown } from 'react-icons/lu'

function parseOptions(children) {
  return React.Children.toArray(children)
    .filter((child) => React.isValidElement(child) && child.type === 'option')
    .map((child, index) => ({
      value: child.props.value ?? '',
      label: child.props.children,
      disabled: Boolean(child.props.disabled),
      key: child.key ?? `${child.props.value}-${index}`,
    }))
}

function SelectIcon({ className, ...props }) {
  return (
    <LuChevronDown
      {...props}
      aria-hidden
      className={`pt-select-chevron ${className || ''}`.trim()}
    />
  )
}

function ProductionToolsSelect({
  name,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  className = '',
  autoComplete,
  required,
  children,
}) {
  const options = useMemo(() => parseOptions(children), [children])
  const placeholderOption = options.find((opt) => !opt.value)
  const selectedOption = options.find((opt) => opt.value === value)
  const hasPlaceholder = Boolean(placeholderOption)
  const isPlaceholder = !selectedOption || (!value && hasPlaceholder)

  const renderValue = (selected) => {
    const option = options.find((opt) => opt.value === selected)
    if (option) return option.label
    if (placeholderOption) return placeholderOption.label
    return options[0]?.label ?? 'Select...'
  }

  return (
    <FormControl
      fullWidth
      disabled={disabled}
      className={`pt-select ${className}`.trim()}
      sx={{ margin: 0 }}
    >
      <Select
        id={id}
        name={name}
        value={value ?? ''}
        onChange={onChange}
        onBlur={onBlur}
        variant="standard"
        disableUnderline
        displayEmpty={hasPlaceholder}
        renderValue={renderValue}
        required={required}
        IconComponent={SelectIcon}
        inputProps={{ autoComplete, name, id }}
        className={`pt-select-trigger${isPlaceholder ? ' is-placeholder' : ''}`}
        MenuProps={{
          disableScrollLock: true,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          PaperProps: {
            sx: {
              maxHeight: '300px',
            },
          },
        }}
        sx={{
          '&::before, &::after': {
            display: 'none',
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            minHeight: 'unset',
            padding: '11px 32px 11px 14px !important',
            boxSizing: 'border-box',
          },
          '& .MuiSelect-icon': {
            right: 14,
            width: 14,
            height: 14,
            top: 'calc(50% - 7px)',
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.key}
            value={option.value}
            disabled={
              option.disabled || (!option.value && Boolean(placeholderOption))
            }
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default ProductionToolsSelect
